import { fetchApi } from '@/services/api-client';

export const LEAVE_REQUESTS_PATH = '/leave-requests';
export const ADMIN_LEAVE_REQUESTS_PATH = '/admin/leave-requests';

export type LeaveType = 'personal' | 'vacation' | 'sick' | 'professional_development';
export type LeaveDayPortion = 'full_day' | 'morning_half' | 'afternoon_half';
export type LeaveStatus = 'pending' | 'approved' | 'rejected';

export type LeaveRequestUser = {
  id: string;
  email: string;
  displayName: string;
  fullName: string;
  photoUrl: string;
};

export type LeaveRequest = {
  id: string;
  requesterId: string;
  requester: LeaveRequestUser | null;
  type: LeaveType;
  startDate: string;
  endDate: string;
  dayPortion: LeaveDayPortion;
  reason: string;
  status: LeaveStatus;
  decisionMessage: string | null;
  reviewerId: string | null;
  reviewer: LeaveRequestUser | null;
  reviewedAt: string | null;
  dayCount: number;
  createdAt: string;
  updatedAt: string;
};

export type LeaveRequestNote = {
  id: string;
  leaveRequestId: string;
  authorId: string;
  author: LeaveRequestUser | null;
  body: string;
  createdAt: string;
  updatedAt: string;
};

export type LeaveRequestDetail = LeaveRequest & {
  notes: LeaveRequestNote[];
};

export type EntitlementBucket = {
  type: LeaveType;
  limit: number | null;
  approvedDays: number;
  pendingDays: number;
  remaining: number | null;
};

export type EntitlementSummary = {
  academicYear: string;
  buckets: EntitlementBucket[];
};

export type CreateLeaveRequestData = {
  type: LeaveType;
  startDate: string;
  endDate: string;
  dayPortion?: LeaveDayPortion;
  reason: string;
};

export type AdminCreateLeaveRequestData = CreateLeaveRequestData & {
  requesterId: string;
};

export type DecideLeaveRequestData = {
  status: 'approved' | 'rejected';
  decisionMessage?: string;
};

export type LeaveRequestFilters = {
  status?: LeaveStatus;
  type?: LeaveType;
  requesterId?: string;
};

/**
 * A colleague's absence as any staff member (admin/teacher/employee) may see it — deliberately
 * not `LeaveRequest`: the API redacts `type`/`reason` structurally for this route, so someone is
 * out is visible to everyone, why is not.
 */
export type StaffAbsence = {
  id: string;
  userId: string;
  displayName: string;
  fullName: string;
  photoUrl: string;
  startDate: string;
  endDate: string;
  dayPortion: LeaveDayPortion;
  status: 'pending' | 'approved';
};

function withQuery(path: string, params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) search.append(key, value);
  }
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

export const leaveRequestsApi = {
  create: (data: CreateLeaveRequestData) =>
    fetchApi<LeaveRequest>(LEAVE_REQUESTS_PATH, { method: 'POST', body: JSON.stringify(data) }),

  getMine: () => fetchApi<LeaveRequest[]>(`${LEAVE_REQUESTS_PATH}/me`),

  mineDetail: (id: string) => fetchApi<LeaveRequest>(`${LEAVE_REQUESTS_PATH}/me/${id}`),

  /** Correcting your own request. The API refuses one that has already been decided. */
  updateMine: (id: string, data: CreateLeaveRequestData) =>
    fetchApi<LeaveRequest>(`${LEAVE_REQUESTS_PATH}/me/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  getStaffAbsences: () => fetchApi<StaffAbsence[]>(`${LEAVE_REQUESTS_PATH}/staff-absences`),

  getMyEntitlement: (year?: string) =>
    fetchApi<EntitlementSummary>(withQuery(`${LEAVE_REQUESTS_PATH}/me/entitlement`, { year })),

  adminCreate: (data: AdminCreateLeaveRequestData) =>
    fetchApi<LeaveRequest>(ADMIN_LEAVE_REQUESTS_PATH, { method: 'POST', body: JSON.stringify(data) }),

  adminList: (filters: LeaveRequestFilters = {}) =>
    fetchApi<LeaveRequest[]>(withQuery(ADMIN_LEAVE_REQUESTS_PATH, filters)),

  adminGet: (id: string) => fetchApi<LeaveRequestDetail>(`${ADMIN_LEAVE_REQUESTS_PATH}/${id}`),

  /** An admin may correct any request, decided or not; the decision itself is left alone. */
  adminUpdate: (id: string, data: CreateLeaveRequestData) =>
    fetchApi<LeaveRequest>(`${ADMIN_LEAVE_REQUESTS_PATH}/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  adminGetByUser: (userId: string, year?: string) =>
    fetchApi<{ requests: LeaveRequest[]; entitlement: EntitlementSummary }>(
      withQuery(`${ADMIN_LEAVE_REQUESTS_PATH}/by-user/${userId}`, { year })
    ),

  adminDecide: (id: string, data: DecideLeaveRequestData) =>
    fetchApi<LeaveRequest>(`${ADMIN_LEAVE_REQUESTS_PATH}/${id}/decision`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  adminAddNote: (id: string, body: string) =>
    fetchApi<LeaveRequestNote>(`${ADMIN_LEAVE_REQUESTS_PATH}/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  adminDeleteNote: (id: string, noteId: string) =>
    fetchApi<{ success: boolean }>(`${ADMIN_LEAVE_REQUESTS_PATH}/${id}/notes/${noteId}`, {
      method: 'DELETE',
    }),
};
